import React, { useState, useEffect, useRef } from 'react';
import { socketService, SocketStatusUpdate, SocketStatus } from '../utils/socket';
import './SocketNotification.css';

interface SocketNotificationProps {
  showOnMount?: boolean;
}

const getStatusMessage = (status: SocketStatus): string => {
  switch (status) {
    case 'connected': return 'Comunicación entre ventanas activa';
    case 'local': return 'Sistema funcionando con localStorage';
    default: return 'Estado desconocido';
  }
};

const getStatusIcon = (status: SocketStatus): string => {
  switch (status) {
    case 'connected': return '✅';
    case 'local': return '🔵';
    default: return '❓';
  }
};

const getStatusClass = (status: SocketStatus): string => {
  switch (status) {
    case 'connected': return 'success';
    case 'local': return 'info';
    default: return 'info';
  }
};

const SocketNotification: React.FC<SocketNotificationProps> = ({ showOnMount = true }) => {
  const [notification, setNotification] = useState<SocketStatusUpdate | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const scheduleHide = () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setIsVisible(false), 5000);
    };

    if (showOnMount) {
      const currentStatus = socketService.getStatus();
      setNotification({
        status: currentStatus,
        message: getStatusMessage(currentStatus),
        timestamp: Date.now()
      });
      setIsVisible(true);
      scheduleHide();
    }

    // Escuchar cambios de estado siempre, también cuando showOnMount=true.
    // Antes un early-return dejaba muerta esta suscripción.
    const unsubscribe = socketService.onStatusChange((statusUpdate) => {
      setNotification(statusUpdate);
      setIsVisible(true);
      scheduleHide();
    });

    return () => {
      unsubscribe();
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [showOnMount]);

  const handleClose = () => {
    setIsVisible(false);
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  if (!notification || !isVisible) return null;

  return (
    <div className={`socket-notification ${getStatusClass(notification.status)} ${isVisible ? 'visible' : ''}`}>
      <div className="notification-content">
        <span className="notification-icon">{getStatusIcon(notification.status)}</span>
        <div className="notification-text">
          <div className="notification-title">
            Estado del Sistema: {notification.status.toUpperCase()}
          </div>
          <div className="notification-message">{notification.message}</div>
        </div>
        <button className="notification-close" onClick={handleClose}>×</button>
      </div>
    </div>
  );
};

export default SocketNotification;
